import { FC, useEffect, useState, useContext } from 'react';
import { UploadAttachmentItem } from './UploadAttachmentItem';
import { RFIAttachment } from '@refineintel/types';
import { LocalizationContext } from 'src/Context/LocalizationContext';
import { UploadManagerErrorsHandler } from './UploadManagerErrorsHandler';
import { Grid, Stack, Typography } from '@mui/material';
import { getFileTypeByExtension } from 'src/Utils/AttachmentUtils';
import { CreateAttachmentResponse, useQueryCreateAttachment } from 'src/hooks/useQueryCreateAttachment';
import { useQueryDeleteAttachment } from 'src/hooks/useQueryDeleteAttachment';
import { errorHandler } from 'src/Config/ErrorHandler';
import { Attachments } from 'src/Branding/config';
import { InvestigationContext } from 'src/Context/InvestigationContext';
import { useLoading } from '@context/LoadingContext';
import { refineFonts } from '@/Theming/refineFonts';
import { REFINE_ANALYTICS_EVENTS } from '@/types/RefineAnalyticsEvents';
import { useAnalytics } from '@hooks/useAnalytics';

interface IUploadedAttachmentsProps {
	newAttachments: File[] | undefined;
}

export const UploadedAttachmentsComponent: FC<IUploadedAttachmentsProps> = ({ newAttachments }) => {
	const { attachments, updateAttachments } = useContext(InvestigationContext);
	const [previewAttachments, setPreviewAttachments] = useState<RFIAttachment[]>([]);
	const [attachmentsQueue, setAttachmentsQueue] = useState<File[]>([]);
	const [errorMessage, setErrorMessage] = useState<string>('');
	const { FileUpload: text } = useContext(LocalizationContext);
	const { FileUpload } = useContext(LocalizationContext);
	const { UploadFilesUnsupportedFormatErrorMessage, UploadFilesMaxSizeExceededError } = FileUpload;
	const { trackRefineEvent } = useAnalytics();

	const {
		createAttachment,
		isLoading: uploadingAttachment,
		errorMessage: createAttachmentErrorMessage,
		resetErrorMessage: resetCreateAttachmentErrorMessage,
	} = useQueryCreateAttachment();

	const {
		isLoading: deletingAttachment,
		deleteAttachment,
		errorMessage: deleteAttachmentError,
		resetErrorMessage: resetDeleteAttachmentErrorMessage,
	} = useQueryDeleteAttachment();

	const { setLoading } = useLoading();

	const removeAttachment = async (id: string, isAbort = false) => {
		if (isAbort) {
			setLoading(false);
		}
		const updatedAttachments = attachments.filter((attachment) => attachment.id !== id);
		updateAttachments(updatedAttachments);
		const deletedAttachmentOperation = await deleteAttachment(id);
		if (deletedAttachmentOperation.status !== 'ok') {
			errorHandler(new Error('Attachment deletion failed'), {
				action: 'removeAttachment',
				attachmentId: id,
				operationError: deletedAttachmentOperation.error,
				isAbort,
				status: deletedAttachmentOperation.status,
			});
		}
	};

	const uploadAttachment = async (files: File[]) => {
		// Filter out unsupported file types
		let supportedFiles = files.filter((file: File) =>
			Attachments.SupportedExtensions.includes(file.name.toLowerCase().substring(file.name.lastIndexOf('.') + 1)),
		);
		// If there are unsupported files, show error message
		if (files.length - supportedFiles.length > 0) {
			setErrorMessage(UploadFilesUnsupportedFormatErrorMessage);
		}
		// Filter out oversized files
		const supportedFilesLength = supportedFiles.length;
		supportedFiles = supportedFiles.filter((file: File) => file.size <= Attachments.AttachmentSizeLimit);
		if (supportedFilesLength - supportedFiles.length > 0) {
			setErrorMessage(UploadFilesMaxSizeExceededError);
		}

		const attachmentsQueueData = [...attachmentsQueue, ...supportedFiles];
		setAttachmentsQueue(attachmentsQueueData);
		// We need local queue to store the data of the files that are being uploaded without state update because state update is async
		const localQueue = attachmentsQueueData;
		const queriesSet = new Set([...localQueue, ...attachmentsQueueData]);
		const localAttachmentsPreview: RFIAttachment[] = Array.from(queriesSet).map((attachment: File) => {
			return {
				fileEncoding: attachment.type,
				fileExtension: getFileTypeByExtension(attachment.name),
				fileMimeType: attachment.type,
				fileName: attachment.name,
				fileSizeB: attachment.size,
				id: attachment.name,
				name: attachment.name,
				parents: [],
				updated: Date.now().toString(),
				isDeleted: false,
			} as unknown as RFIAttachment;
		});

		// Update the preview list (files in uploading state)
		setPreviewAttachments(localAttachmentsPreview);

		const updatedAttachments: RFIAttachment[] = [...attachments];
		for (const attachmentData of localQueue) {
			const createAttachmentResponse: CreateAttachmentResponse | null = await createAttachment(attachmentData);
			if (!createAttachmentResponse) {
				throw new Error('createAttachmentResponse is null');
			}

			const { attachmentId: createdAttachmentId, attachmentData: createdAttachment } = createAttachmentResponse;

			if (!createdAttachmentId) {
				errorHandler(new Error('Attachment creation failed'), {
					action: 'createAttachment',
					attachmentData: {
						filename: attachmentData.name,
						size: attachmentData.size,
						type: attachmentData.type,
					},
				});
				return;
			}

			trackRefineEvent(REFINE_ANALYTICS_EVENTS.ATTACHMENT_UPLOADED, {
				fileSize: attachmentData.size,
				fileType: attachmentData.type,
			});
			// Remove the attachment from the attachments queue
			const updatedQueue = attachmentsQueue.filter((query) => query.name !== attachmentData.name);
			setAttachmentsQueue(updatedQueue);
			// Update the attachment ID with value from the server
			const uploadedFile =
				localAttachmentsPreview[
					localAttachmentsPreview.findIndex(
						(attachment) => attachment.name === attachmentData.name && attachment.id === attachment.name,
					)
				];
			// Update the attachment ID with value from the server
			uploadedFile.id = createdAttachmentId;
			uploadedFile.name = createdAttachment.name;
			uploadedFile.fileName = createdAttachment.name;
			uploadedFile.fileExtension = createdAttachment.fileExtension;
			uploadedFile.fileEncoding = createdAttachment.fileEncoding;
			uploadedFile.fileMimeType = createdAttachment.fileMimeType;
			uploadedFile.fileSizeB = createdAttachment.fileSizeB;
			uploadedFile.parents = createdAttachment.parents;
			uploadedFile.updated = createdAttachment.updated;
			uploadedFile.isDeleted = createdAttachment.isDeleted;
			const localStoredAttachmentsToAbort = JSON.parse(localStorage.getItem('attachmentsToAbort') || '[]');
			if (!localStoredAttachmentsToAbort.includes(attachmentData.name)) {
				updatedAttachments.push(uploadedFile);
				updateAttachments(updatedAttachments);
				// Remove the preview from the preview list
				setPreviewAttachments((prev) =>
					prev.filter((prevAttachment) => prevAttachment.name !== attachmentData.name),
				);
			} else {
				removeAttachment(createdAttachmentId, true).then(() => {
					localStorage.setItem(
						'attachmentsToAbort',
						JSON.stringify(
							localStoredAttachmentsToAbort.filter(
								(attachment: string) => attachment !== attachmentData.name,
							),
						),
					);
				});
			}
		}
	};

	const resetErrorMessage = () => {
		setErrorMessage('');
		resetCreateAttachmentErrorMessage();
		resetDeleteAttachmentErrorMessage();
	};

	// Add new uploaded attachment
	useEffect(() => {
		if (newAttachments) {
			uploadAttachment(newAttachments);
		}
	}, [newAttachments]);

	// Update the isUploading or isDeleting state to parent
	useEffect(() => {
		setLoading(uploadingAttachment || deletingAttachment);
	}, [uploadingAttachment, deletingAttachment]);

	const abortAttachmentUpload = (attachmentName: string) => {
		setLoading(false);
		setPreviewAttachments((prev) => prev.filter((attachment) => attachment.name !== attachmentName));
		const storedAttachmentsToAbort = JSON.parse(localStorage.getItem('attachmentsToAbort') || '[]');
		localStorage.setItem('attachmentsToAbort', JSON.stringify([...storedAttachmentsToAbort, attachmentName]));
	};

	return (
		<Stack gap={'16px'}>
			{/* Error Handler */}
			<UploadManagerErrorsHandler
				resetErrors={resetErrorMessage}
				errorMessage={errorMessage || createAttachmentErrorMessage || deleteAttachmentError}
			/>

			{/* Files Uploaded Header */}
			{attachments.length + previewAttachments.length > 0 && (
				<Typography sx={{ ...refineFonts.mediumBold }}>
					{text.FilesUploadedHeader.replace('%1$s', `${attachments.length}`) || 'Files uploaded'}
				</Typography>
			)}

			{/* Attachments */}
			<Grid container spacing={2}>
				{[...previewAttachments, ...attachments].map((attachment: RFIAttachment) => {
					const { id, name, fileSizeB, fileExtension } = attachment;
					const fileType = getFileTypeByExtension(fileExtension);
					return (
						<Grid key={id} size="auto">
							<UploadAttachmentItem
								deleteAttachment={removeAttachment}
								attachmentId={id}
								attachmentName={name}
								attachmentSize={fileSizeB}
								attachmentType={fileType}
								abortAttachmentUpload={abortAttachmentUpload}
								isLoading={id === name} // If the ID is the same as the name, it means it's still uploading
							/>
						</Grid>
					);
				})}
			</Grid>
		</Stack>
	);
};
